import type { ReactNode } from "react";
import {
  SYSTEM_LOCALITY,
  SYSTEM_LOGO_PATH,
  SYSTEM_PORTAL_TITLE,
} from "../../app/branding";
import {
  MARTYRS_SEARCH_PATH,
  PORTAL_HOME_PATH,
  STAFF_LOGIN_PATH,
} from "./publicPortalRoutes";
import { Link, useLocation } from "react-router-dom";
import "../../styles/public-tokens.css";
import "./publicPortal.css";

/**
 * S11.11.1 — khung chung của cổng tưởng niệm công khai (Spec §5.1, §5.3, §5.5, §6.1).
 *
 * Khung sở hữu nav và chân trang ngoài `children`. Trang chủ giữ ghi chú nguồn số liệu
 * cạnh dashboard nên không lặp cảnh báo ở footer; các màn khác giữ thông báo hiện hành.
 * Nội dung từng màn được truyền qua `children`, không dựng lại trong khung.
 *
 * Nav chỉ chứa hai đích có thật (Spec §5.3): trang cổng và lối vào đăng nhập cán bộ.
 * dựng — kể cả dạng chữ thuần, vì nhãn không bấm được còn tệ hơn không có nhãn. Đây cũng là lý do
 * chân trang chỉ có chữ: mọi `a[href]` trong tài liệu phải thuộc đúng tập ba đích đó.
 */

export type PublicPortalLayoutProps = Readonly<{
  children: ReactNode;
  /**
   * S11.12.3 — `"immersive"` bỏ **cả** header lẫn chân trang của khung, chỉ còn `shell` + `main`.
   *
   * Trang tham quan cần điều đó vì hai lý do, cả hai đều là ràng buộc cứng:
   * - khung 3D phải chạy hết viewport;
   * - nav của nó do `PublicTourChrome` dựng — nơi có chỗ cho nút ngăn kéo và nút ẩn giao diện, tức
   *   là state của viewer mà khung này không giữ. Nếu khung cũng render header thì trang có **hai**
   *   nav, và không test nào bắt được lỗi câm đó.
   *
   * Mặc định `"default"`: mọi caller cũ giữ nguyên hành vi, kể cả khi quên truyền.
   */
  variant?: "default" | "immersive";
}>;

/**
 * Bọc một màn công khai trong khung cổng: nav dính ở đầu trang, nội dung màn ở giữa, chân trang ba
 * vế ở cuối. Chân trang là con TRỰC TIẾP của khung (không lồng trong `main`/`section`) để giữ
 * đúng role `contentinfo`.
 *
 * Ở variant `"immersive"`, khung chỉ còn lại đúng vùng `main`: xem doc của `variant`.
 */
export function PublicPortalLayout(props: PublicPortalLayoutProps): ReactNode {
  const immersive = props.variant === "immersive";
  const location = useLocation();
  const homepage = location.pathname === PORTAL_HOME_PATH;
  const isSearchPage = location.pathname === MARTYRS_SEARCH_PATH;

  return (
    <div className={immersive ? "pub-shell pub-shell-immersive" : "pub-shell"}>
      {immersive ? null : (
        <header className="pub-header">
          <nav className="pub-nav" aria-label="Điều hướng cổng thông tin">
            <Link className="pub-brand" to={PORTAL_HOME_PATH}>
              {/* Dấu nhận diện đã ẩn khỏi tên đọc: tên cổng mới là thứ có nghĩa với người dùng AT. */}
              <span className="pub-brand-mark" aria-hidden="true">
                <img src={SYSTEM_LOGO_PATH} alt="" width="30" height="30" />
              </span>
              <span className="pub-brand-copy">
                <span className="pub-brand-name">{SYSTEM_PORTAL_TITLE}</span>
                <span className="pub-brand-kicker">{SYSTEM_LOCALITY}</span>
              </span>
            </Link>
            <ul className="pub-nav-links">
              <li>
                <Link
                  className={`pub-nav-link${homepage ? " pub-nav-link-active" : ""}`}
                  to={PORTAL_HOME_PATH}
                >
                  Trang chủ
                </Link>
              </li>
              <li>
                <Link
                  className={`pub-nav-link${isSearchPage ? " pub-nav-link-active" : ""}`}
                  to={MARTYRS_SEARCH_PATH}
                >
                  Danh sách liệt sĩ
                </Link>
              </li>
              <li>
                <a className="pub-nav-link pub-nav-link-staff" href={STAFF_LOGIN_PATH}>
                  Đăng nhập
                </a>
              </li>
            </ul>
          </nav>
        </header>
      )}

      {/* Khung đã sở hữu thẻ `main` (đích của skip link trong `index.html`): màn công khai truyền
          vào bằng `<section>`, không mở `main` thứ hai. */}
      <main className="pub-main" id="main-content">
        {props.children}
      </main>

      {immersive ? null : (
        <footer className="pub-footer">
          <div className="pub-footer-inner">
            <section className="pub-footer-brand" aria-label="Giới thiệu cổng">
              <img className="pub-footer-logo" src={SYSTEM_LOGO_PATH} alt="" width="36" height="36" />
              <div className="pub-footer-brand-copy">
                <strong>{SYSTEM_PORTAL_TITLE}</strong>
                <span className="pub-brand-kicker">{SYSTEM_LOCALITY}</span>
                <p>Tưởng nhớ và tri ân các anh hùng liệt sĩ.</p>
              </div>
            </section>
          </div>
          <div className="pub-footer-meta">
            <span>© {new Date().getFullYear()} {SYSTEM_PORTAL_TITLE} · {SYSTEM_LOCALITY}</span>
            {homepage ? null : (
              <>
                <span>Chế độ công khai · chỉ hiển thị thông tin đã được công bố.</span>
                <span>Dữ liệu mô phỏng, không thay thế hồ sơ đã được phê duyệt.</span>
              </>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
